import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { settingsApi, ApiKey, ApiKeyGenerated } from '../services/api';
import './Settings.css';

interface SettingsProps {
  onClose: () => void;
}

export const Settings: React.FC<SettingsProps> = ({ onClose }) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [generatedKey, setGeneratedKey] = useState<ApiKeyGenerated | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const keys = await settingsApi.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    try {
      const newKey = await settingsApi.generateApiKey('Mobile Device');
      setGeneratedKey(newKey);
      setApiKeys(prev => [
        { 
          id: newKey.id, 
          key_prefix: newKey.key_prefix, 
          name: newKey.name, 
          created_at: newKey.created_at, 
          is_active: true 
        }, 
        ...prev
      ]);
      toast.success('API key generated successfully');
    } catch (error) {
      console.error('Failed to generate API key:', error);
      toast.error('Failed to generate API key');
    }
  };

  const handleRegenerateKey = async () => {
    try {
      const newKey = await settingsApi.regenerateApiKey('Mobile Device');
      setGeneratedKey(newKey);
      await fetchApiKeys(); // Refresh the list
      toast.success('API key regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      toast.error('Failed to regenerate API key');
    }
  };

  const handleDeleteKey = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await settingsApi.deleteApiKey(id);
      setApiKeys(prev => prev.filter(key => key.id !== id));
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const generateQRCodeUrl = (apiKey: string, serverUrl: string) => {
    // Create the shortcut configuration data
    const shortcutConfig = {
      api_key: apiKey,
      server_url: serverUrl,
      share_endpoint: `${serverUrl}/api/mobile/share`
    };
    
    const configData = encodeURIComponent(JSON.stringify(shortcutConfig));
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${configData}`;
  };

  const serverUrl = process.env.REACT_APP_API_URL || window.location.origin;
  const cleanServerUrl = serverUrl.replace('/api', '');

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>Settings</h2>
        <button onClick={onClose} className="close-button">×</button>
      </div>

      <div className="settings-content">
        <section className="api-keys-section">
          <h3>Mobile Share API Keys</h3>
          <p>Generate API keys to enable sharing content from iOS devices using Shortcuts.</p>

          {generatedKey && (
            <div className="generated-key-alert">
              <h4>New API Key Generated</h4>
              <p>Save this key securely - it won't be shown again:</p>
              <div className="key-display">
                <code>{generatedKey.key}</code>
                <button 
                  onClick={() => copyToClipboard(generatedKey.key)}
                  className="copy-button"
                >
                  Copy
                </button>
              </div>
              <div className="qr-section">
                <button 
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="qr-toggle-button"
                >
                  {showQRCode ? 'Hide' : 'Show'} QR Code for iOS Setup
                </button>
                {showQRCode && (
                  <div className="qr-code-container">
                    <img 
                      src={generateQRCodeUrl(generatedKey.key, cleanServerUrl)}
                      alt="QR Code for iOS Shortcuts setup"
                      className="qr-code"
                    />
                    <p className="qr-instructions">
                      Scan this QR code with your iOS device to automatically configure the Brain Dump shortcut.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="api-key-actions">
            {apiKeys.length === 0 ? (
              <button onClick={handleGenerateKey} className="generate-button">
                Generate First API Key
              </button>
            ) : (
              <button onClick={handleRegenerateKey} className="regenerate-button">
                Regenerate API Key
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="loading">Loading API keys...</div>
          ) : (
            <div className="api-keys-list">
              {apiKeys.length === 0 ? (
                <div className="empty-state">
                  <p>No API keys generated yet. Create one to enable mobile sharing.</p>
                </div>
              ) : (
                apiKeys.map((key) => (
                  <div key={key.id} className="api-key-item">
                    <div className="key-info">
                      <div className="key-name">{key.name}</div>
                      <div className="key-preview">{key.key_prefix}</div>
                      <div className="key-meta">
                        <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                        {key.last_used_at && (
                          <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                        )}
                        <span className={`status ${key.is_active ? 'active' : 'inactive'}`}>
                          {key.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="key-actions">
                      <button 
                        onClick={() => handleDeleteKey(key.id)}
                        className="delete-button"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        <section className="instructions-section">
          <h3>iOS Shortcuts Setup</h3>
          <div className="setup-steps">
            <div className="step">
              <h4>Step 1: Generate API Key</h4>
              <p>Generate an API key above if you haven't already.</p>
            </div>
            <div className="step">
              <h4>Step 2: Install Shortcuts App</h4>
              <p>Make sure you have the Shortcuts app installed on your iOS device.</p>
            </div>
            <div className="step">
              <h4>Step 3: Create Brain Dump Shortcut</h4>
              <p>Either scan the QR code above or manually create a shortcut with these settings:</p>
              <ul>
                <li>Server URL: <code>{cleanServerUrl}</code></li>
                <li>API Endpoint: <code>/api/mobile/share</code></li>
                <li>API Key: Use the generated key above</li>
              </ul>
            </div>
            <div className="step">
              <h4>Step 4: Enable Share Sheet</h4>
              <p>Configure the shortcut to appear in the Share Sheet for easy access from any app.</p>
            </div>
          </div>
        </section>
      </div>

    </div>
  );
};